'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronsDown } from 'lucide-react';
import { useDeviceType } from '@/hooks/useIsMobile';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';

type TopHeroProps = {
    onScrollClick: () => void;
};

export default function TopHero({ onScrollClick }: TopHeroProps) {
    const { isMobile } = useDeviceType();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [videoSrc, setVideoSrc] = useState('/assets/top/hero.mp4');
    const startAtRef = useRef(17.0);

    useEffect(() => {
        const isD = Math.random() < 0.3;
        startAtRef.current = isD ? 0 : 17.0;
        setVideoSrc(isD ? '/assets/top/hero_d.mp4' : '/assets/top/hero.mp4');
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const seek = () => {
            if (video.currentTime < startAtRef.current) {
                video.currentTime = startAtRef.current;
            }
        };

        video.addEventListener('loadedmetadata', seek);
        video.addEventListener('canplay', seek);

        if (video.readyState >= 1) {
            seek();
        }

        return () => {
            video.removeEventListener('loadedmetadata', seek);
            video.removeEventListener('canplay', seek);
        };
    }, [videoSrc]);

    return (
        <div
            className="relative flex w-full overflow-hidden rounded-t-md"
            style={{
                height: isMobile ? 'calc(100dvh - 15px)' : 'calc(100vh)',
                transition: 'height 0.3s ease',
            }}
        >
            {videoSrc && (
                <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    src={videoSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            )}

            {/* 静止フィルター */}
            <div className="absolute inset-0 z-20 pointer-events-none bg-black/30" aria-hidden />

            <div className="absolute inset-0 z-20 flex flex-col justify-center items-center lg:items-end p-8 select-none pointer-events-none">
                <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, ease: 'easeOut', delay: 1.0 }}
                    className="mt-0 md:mt-[200px] font-cantarell font-bold text-color-white-base text-[30px] lg:text-[70px]
                     drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] will-change-transform [transform:translateZ(0)] [backface-visibility:hidden]"
                >
                    <ImageAdaptive src="/images/top/hero-logo.png" baseWidth={300} breakpoints={{ md: 450 }} />
                </motion.div>

                <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, ease: 'easeOut', delay: 1.1 }}
                    className="flex flex-col items-end mt-0 md:-mt-[20px] text-color-white-base
                     drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] will-change-transform [transform:translateZ(0)] [backface-visibility:hidden]"
                >
                    <div className="font-cantarell font-medium text-[11px] lg:text-[13px]">
                        SchooMy fox. is a portal site that fosters creativity
                    </div>
                    <div className="font-cantarell font-medium text-[11px] lg:text-[13px]">
                        and hands-on skills through SchooMy Board and eDIY.
                    </div>
                </motion.div>
            </div>

            <div className="absolute bottom-40 left-1/2 -translate-x-1/2 z-30">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, y: [0, -10, 0] }}
                    transition={{
                        opacity: { duration: 0.8, delay: 0.3 },
                        y: { repeat: Infinity, repeatType: 'loop', duration: 1.2, ease: 'easeInOut' },
                    }}
                >
                    <button type="button" onClick={onScrollClick} className="transition focus:outline-none">
                        <IconAdaptive
                            Icon={ChevronsDown}
                            baseSize={40}
                            breakpoints={{ lg: 60 }}
                            className="text-color-white-base/50 hover:text-color-white-base transition"
                        />
                    </button>
                </motion.div>
            </div>
        </div>
    );
}
